import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { Trash2, Plus, Facebook, Instagram, Music2, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";

const PlatformBadge = ({ p }) => {
  const cfg = {
    facebook: { cls: "platform-facebook", Icon: Facebook },
    instagram: { cls: "platform-instagram", Icon: Instagram },
    tiktok: { cls: "platform-tiktok", Icon: Music2 },
  }[p] || { cls: "bg-zinc-300", Icon: Facebook };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white px-2 py-0.5 ${cfg.cls}`} style={{borderRadius: 2}}>
      <cfg.Icon size={10}/> {p}
    </span>
  );
};

export default function Posts() {
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", platforms: [], groups: "", scheduled_at: "", status: "draft" });
  const [bulk, setBulk] = useState({ title: "", content: "", platforms: [], groups: "", scheduled_at: "" });

  const load = async () => { const { data } = await api.get("/posts"); setPosts(data); };
  useEffect(() => { load(); }, []);

  const togglePlat = (obj, setObj, p) => {
    setObj({ ...obj, platforms: obj.platforms.includes(p) ? obj.platforms.filter((x) => x !== p) : [...obj.platforms, p] });
  };

  const save = async () => {
    if (!form.title || !form.content || form.platforms.length === 0) return toast.error("Missing fields");
    const payload = {
      title: form.title,
      content: form.content,
      platforms: form.platforms,
      groups: form.groups.split(",").map((s) => s.trim()).filter(Boolean),
      scheduled_at: form.scheduled_at || null,
      status: form.scheduled_at ? "scheduled" : (form.status || "draft"),
    };
    await api.post("/posts", payload);
    toast.success("OK");
    setOpen(false);
    setForm({ title: "", content: "", platforms: [], groups: "", scheduled_at: "", status: "draft" });
    load();
  };

  const doBulk = async () => {
    const groups = bulk.groups.split(",").map((s) => s.trim()).filter(Boolean);
    if (!bulk.title || !bulk.content || bulk.platforms.length === 0 || groups.length === 0) return toast.error("Missing fields");
    const { data } = await api.post("/posts/bulk", {
      title: bulk.title, content: bulk.content, platforms: bulk.platforms, groups, scheduled_at: bulk.scheduled_at || null,
    });
    toast.success(`${data.created} posts`);
    setBulkOpen(false);
    setBulk({ title: "", content: "", platforms: [], groups: "", scheduled_at: "" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/posts/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="posts-root">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("social_posts")}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("social_posts")}</h1>
        </div>
        <div className="flex gap-2">
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <button data-testid="open-bulk" className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-300 hover:bg-zinc-50 font-medium" style={{borderRadius: 2}}>
                <Layers size={16}/> {t("bulk_post")}
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("bulk_post")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input data-testid="bulk-title" placeholder={t("title")} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={bulk.title} onChange={(e) => setBulk({...bulk, title: e.target.value})}/>
                <textarea data-testid="bulk-content" placeholder={t("content")} rows={5} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={bulk.content} onChange={(e) => setBulk({...bulk, content: e.target.value})}/>
                <div className="flex gap-2 flex-wrap">
                  {["facebook", "instagram", "tiktok"].map((p) => (
                    <button key={p} type="button" onClick={() => togglePlat(bulk, setBulk, p)} data-testid={`bulk-plat-${p}`} className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${bulk.platforms.includes(p) ? "klein-blue border-[#002FA7]" : "bg-white border-zinc-300"}`} style={{borderRadius: 2}}>{p}</button>
                  ))}
                </div>
                <input data-testid="bulk-groups" placeholder={t("groups_csv")} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={bulk.groups} onChange={(e) => setBulk({...bulk, groups: e.target.value})}/>
                <input type="datetime-local" className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={bulk.scheduled_at} onChange={(e) => setBulk({...bulk, scheduled_at: e.target.value})}/>
              </div>
              <DialogFooter>
                <button data-testid="bulk-submit" onClick={doBulk} className="btn-primary">{t("save")}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="open-new-post" className="btn-primary inline-flex items-center gap-2"><Plus size={16}/> {t("add_post")}</button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("add_post")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input data-testid="post-title" placeholder={t("title")} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}/>
                <textarea data-testid="post-content" placeholder={t("content")} rows={6} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.content} onChange={(e) => setForm({...form, content: e.target.value})}/>
                <div className="flex gap-2 flex-wrap">
                  {["facebook", "instagram", "tiktok"].map((p) => (
                    <button key={p} type="button" onClick={() => togglePlat(form, setForm, p)} data-testid={`post-plat-${p}`} className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${form.platforms.includes(p) ? "klein-blue border-[#002FA7]" : "bg-white border-zinc-300"}`} style={{borderRadius: 2}}>{p}</button>
                  ))}
                </div>
                <input data-testid="post-groups" placeholder={t("groups_csv")} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.groups} onChange={(e) => setForm({...form, groups: e.target.value})}/>
                <div className="grid grid-cols-2 gap-2">
                  <input type="datetime-local" className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.scheduled_at} onChange={(e) => setForm({...form, scheduled_at: e.target.value})}/>
                  <select className="w-full border border-zinc-300 px-3 py-2 bg-white" style={{borderRadius: 2}} value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                    <option value="draft">{t("drafts")}</option>
                    <option value="scheduled">{t("scheduled")}</option>
                    <option value="published">{t("published")}</option>
                  </select>
                </div>
              </div>
              <DialogFooter>
                <button data-testid="post-save" onClick={save} className="btn-primary">{t("save")}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="swiss-card divide-y divide-zinc-100">
        {posts.length === 0 && <div className="p-8 text-zinc-500">{t("no_data")}</div>}
        {posts.map((p) => (
          <div key={p.id} className="p-5 flex items-start gap-4" data-testid={`post-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-bold text-lg">{p.title}</h3>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-zinc-100" style={{borderRadius: 2}}>{t(p.status)}</span>
              </div>
              <p className="text-sm text-zinc-600 mt-1 line-clamp-2 whitespace-pre-wrap">{p.content}</p>
              <div className="flex gap-2 flex-wrap mt-3">
                {p.platforms.map((pl) => <PlatformBadge key={pl} p={pl}/>)}
                {p.groups && p.groups.length > 0 && <span className="text-xs text-zinc-500">· {p.groups.join(", ")}</span>}
              </div>
              {p.scheduled_at && <div className="text-xs text-zinc-500 mt-1">{t("scheduled_at")}: {new Date(p.scheduled_at).toLocaleString()}</div>}
            </div>
            <button onClick={() => del(p.id)} data-testid={`del-post-${p.id}`} className="p-2 hover:bg-red-50 text-zinc-500 hover:text-[#E63946]" style={{borderRadius: 2}}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
